import NextImage from 'next/image';
import groupBy from 'lodash/groupBy';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Avatar,
  Box,
  Center,
  Flex,
  Heading,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { format, fromUnixTime } from 'date-fns';
import { useTranslation } from 'next-i18next';

import EmailSubscribe from '@components/chakra_pro/EmailSubscribe';

import useDateFnsLocale from '../../lib/hooks/useDateFnsLocale';
import { EventTypeFromKaik } from '../../lib/types';
import { NUTSHELL_LIVE_BLUE } from '../../constants';

import EventCard from './EventCard';

type Props = {
  username: string;
  name: string;
  coverPhoto: string;
  avatar: string;
  bio: string;
  url: string;
  primaryColor: string;
  events: EventTypeFromKaik[];
};

export default function UserContainer(props: Props) {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();

  const { username, name, coverPhoto, avatar, bio, events, primaryColor } =
    props;

  const groupedEventsByDate = groupBy(
    (events || []).map((event) => ({
      // Append additional key for groupBy
      ...event,
      formattedStartedAt: format(fromUnixTime(event.startedAt), 'ccc, PPP', {
        locale: dateFnsLocale,
      }),
    })),
    'formattedStartedAt'
  );

  return (
    <Center py={4} px={2}>
      <Box
        maxW={'600px'}
        w={'full'}
        bg={useColorModeValue('#FAFAFA', 'gray.800')}
        boxShadow={'2xl'}
        rounded={'2xl'}
        overflow={'hidden'}
        pb={8}
      >
        {coverPhoto ? (
          <NextImage
            width="1200px"
            height="630px"
            src={coverPhoto}
            objectFit={'cover'}
          />
        ) : (
          <Box
            width="600px"
            height="337.5px" // 16:9
            bg={primaryColor || 'brand.500'}
          />
        )}
        <Flex justify={'center'} mt={-12}>
          <Avatar
            size={'xl'}
            src={avatar}
            name={name}
            backgroundColor={'white'}
            color={primaryColor}
            css={{
              border: '2px solid white',
            }}
            shadow="md"
          />
        </Flex>
        <Box p={6} pb={2}>
          <Heading
            textAlign="center"
            fontSize={'2xl'}
            fontWeight={500}
            fontFamily={'body'}
          >
            {name}
          </Heading>
          <Text textAlign="center" color={'gray.500'}>
            @{username}
          </Text>
          <Text
            my={4}
            color={'gray.500'}
            whiteSpace="pre-wrap"
            textAlign="center"
            width="60%"
            mx="auto"
          >
            {bio}
          </Text>

          {Object.keys(groupedEventsByDate).length > 0 ? (
            <Box>
              {Object.entries(groupedEventsByDate).map(
                ([formattedDate, events]) => (
                  <Box key={formattedDate} my={8}>
                    <Heading
                      color="blue.400"
                      fontWeight="normal"
                      fontSize="sm"
                      mb={4}
                    >
                      {formattedDate}
                    </Heading>
                    {events.map((event) => (
                      <EventCard key={event.name} {...event} />
                    ))}
                  </Box>
                )
              )}
            </Box>
          ) : (
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              bg="blackAlpha.100"
              borderRadius="md"
            >
              <AlertIcon
                boxSize="40px"
                mr={0}
                color={primaryColor || NUTSHELL_LIVE_BLUE}
              />
              <AlertTitle
                mt={4}
                mb={1}
                fontSize="lg"
                fontWeight="normal"
                color={primaryColor || NUTSHELL_LIVE_BLUE}
              >
                {t('The event is coming soon, so stay tuned')}
              </AlertTitle>
            </Alert>
          )}
        </Box>
        <EmailSubscribe name={name} slug={username} />
      </Box>
    </Center>
  );
}
